import { computed, ref, type Ref } from 'vue'
import { message } from 'ant-design-vue'
import { batchPreviewThirdParty, batchToggleThirdParty } from '@/api/goodsSetting'
import { MIN_PRICE_RATIO_PERCENT } from '../services/thirdPartyPricing'
import { isProdTypeSupported } from '../services/thirdPartyChannel'
import { getChannelFieldValues, getThirdPartyChannels } from '@/components/channelStatus/channelStatus'

interface Options {
  selectedItems: Ref<goodsSetting.tableDataItem[]>
  prodChnl: Ref<string>
  clearSelection: () => void
  refresh: () => void | Promise<void>
}

const parseChannelPrices = (
  raw: goodsSetting.tableDataItem['channelPrices'],
): goodsSetting.ChannelPrices | undefined => {
  if (!raw) return undefined
  if (typeof raw !== 'string') return raw
  try {
    return JSON.parse(raw)
  } catch {
    return undefined
  }
}

// 审核中 / 变更中 / 不可修改的商品不参与批量开启，与行内编辑、停用等操作对齐
const APPROVAL_STATUS_IN_REVIEW = 10
const REPLICA_STATUS_IN_PROGRESS = 1
const MOD_STATUS_LOCKED = 0
const PREVIEW_MAX_IDS = 100
// modStatus 取默认渠道与所有已开启三方渠道的值，任一渠道被锁定即不可操作；
// 商品类型按目标三方渠道过滤（Xsolla 消耗型/非消耗型，EnjoyPay 仅消耗型），开启与关闭同口径
const isOperable = (
  item: goodsSetting.tableDataItem,
  prodChnl: string,
  payChnl: goodsSetting.ThirdPartyPayChannel,
): boolean =>
  isProdTypeSupported(payChnl, item.prodType) &&
  item.approvalStatus !== APPROVAL_STATUS_IN_REVIEW &&
  item.replicaStatus !== REPLICA_STATUS_IN_PROGRESS &&
  getChannelFieldValues(item, 'modStatus', prodChnl).every((value) => value !== MOD_STATUS_LOCKED)

export default function useThirdPartyBatchToggle(options: Options) {
  const modalOpen = ref(false)
  const activePayChnl = ref<goodsSetting.ThirdPartyPayChannel>('xsolla_pay')
  const priceRatio = ref<number | null>(null)
  const previewLoading = ref(false)
  const submitLoading = ref(false)
  const preview = ref<goodsSetting.ThirdPartyBatchPreviewResult | null>(null)
  // 并发预览用请求序号丢弃过期响应，避免旧结果覆盖新结果
  let previewSeq = 0

  // 当前渠道支持的三方支付清单：苹果是 Xsolla / Enjoy，谷歌只有 Xsolla
  const channels = computed(() => getThirdPartyChannels(options.prodChnl.value))
  const showThirdPartyToggle = computed(() => channels.value.length > 0)

  const countsOf = (idsOf: (payChnl: goodsSetting.ThirdPartyPayChannel) => number[]) =>
    channels.value.reduce<Record<string, number>>((acc, channel) => {
      acc[channel.key] = idsOf(channel.key as goodsSetting.ThirdPartyPayChannel).length
      return acc
    }, {})

  // 目标渠道未开启即可计入，包括当前另一渠道已开启的商品（互斥切换由后端在同一草稿中完成）
  const enableIdsOf = (payChnl: goodsSetting.ThirdPartyPayChannel) =>
    options.selectedItems.value
      .filter((item) => isOperable(item, options.prodChnl.value, payChnl))
      .filter((item) => parseChannelPrices(item.channelPrices)?.priceInfo?.[payChnl]?.enableThirdPay !== true)
      .map((item) => item.id)

  const enableCounts = computed(() => countsOf(enableIdsOf))

  // 只有当前实际开启该渠道的商品才计入关闭；历史异常双开数据会同时计入两边，以后端互斥修复为准
  const disableIdsOf = (payChnl: goodsSetting.ThirdPartyPayChannel) =>
    options.selectedItems.value
      .filter((item) => isOperable(item, options.prodChnl.value, payChnl))
      .filter((item) => parseChannelPrices(item.channelPrices)?.priceInfo?.[payChnl]?.enableThirdPay === true)
      .map((item) => item.id)

  const disableCounts = computed(() => countsOf(disableIdsOf))

  const activeIds = computed(() => Array.from(new Set(enableIdsOf(activePayChnl.value))))

  // 存在缺价商品时才展示比例输入
  const showPriceRatio = computed(() => (preview.value?.generatedPriceCount ?? 0) > 0)
  const isRatioValid = computed(
    () =>
      priceRatio.value != null &&
      Number.isInteger(priceRatio.value) &&
      priceRatio.value >= MIN_PRICE_RATIO_PERCENT &&
      priceRatio.value <= 100,
  )
  const confirmCount = computed(() => preview.value?.operableCount ?? 0)
  const canConfirm = computed(
    () => !previewLoading.value && confirmCount.value > 0 && (!showPriceRatio.value || isRatioValid.value),
  )

  const runPreview = async () => {
    const productIds = activeIds.value
    if (!productIds.length) return
    const seq = ++previewSeq
    previewLoading.value = true
    try {
      const params: goodsSetting.ThirdPartyBatchPreviewParams = {
        prodChnl: options.prodChnl.value,
        payChnl: activePayChnl.value,
        productIds,
      }
      // 首次预览不带比例；用户填写合法比例后才带上
      if (isRatioValid.value) params.priceRatio = priceRatio.value as number
      const res = await batchPreviewThirdParty(params)
      if (seq !== previewSeq) return
      if (res?.code === '0') preview.value = res.data ?? null
    } catch (error) {
      console.error('批量开启三方支付预览失败', error)
    } finally {
      if (seq === previewSeq) previewLoading.value = false
    }
  }

  const openModal = async (payChnl: goodsSetting.ThirdPartyPayChannel) => {
    const ids = Array.from(new Set(enableIdsOf(payChnl)))
    if (!ids.length) return
    // 预览与执行接口约定去重后 1~100 条
    if (ids.length > PREVIEW_MAX_IDS) {
      message.warning(`一次最多开启 ${PREVIEW_MAX_IDS} 个商品，请减少勾选数量`)
      return
    }
    activePayChnl.value = payChnl
    priceRatio.value = null
    preview.value = null
    modalOpen.value = true
    await runPreview()
  }

  // 谷歌不走价格比例预览：勾选项直接提交开启，价格由后端按商品现价处理
  const enableDirectly = async (payChnl: goodsSetting.ThirdPartyPayChannel) => {
    const ids = Array.from(new Set(enableIdsOf(payChnl)))
    if (!ids.length || submitLoading.value) return

    submitLoading.value = true
    try {
      const res = await batchToggleThirdParty({
        prodChnl: options.prodChnl.value,
        productList: ids.map((id) => ({ id, payChnl, enableThirdPay: true })),
      })
      if (!res || res.code !== '0') return
      message.success('操作成功，商品需重新提交审核后才会生效')
      options.clearSelection()
      await options.refresh()
    } catch (error) {
      console.error('批量开启三方支付失败', error)
    } finally {
      submitLoading.value = false
    }
  }

  // 谷歌直接提交，苹果走价格比例预览弹窗
  const startEnable = (payChnl: goodsSetting.ThirdPartyPayChannel) =>
    options.prodChnl.value === 'google' ? enableDirectly(payChnl) : openModal(payChnl)

  const closeModal = () => {
    modalOpen.value = false
    previewSeq += 1
    previewLoading.value = false
  }

  const confirm = async () => {
    if (!canConfirm.value || submitLoading.value) return
    const productList = (preview.value?.items || [])
      .filter((item) => item.priceProcess !== 'NOT_OPERABLE')
      .map((item) => ({ id: item.productId, payChnl: activePayChnl.value, enableThirdPay: true }))
    if (!productList.length) return

    submitLoading.value = true
    try {
      const params: goodsSetting.ThirdPartyBatchToggleParams = {
        prodChnl: options.prodChnl.value,
        productList,
      }
      if (showPriceRatio.value && isRatioValid.value) params.priceRatio = priceRatio.value as number
      const res = await batchToggleThirdParty(params)
      if (!res || res.code !== '0') return
      message.success('操作成功')
      modalOpen.value = false
      options.clearSelection()
      await options.refresh()
    } catch (error) {
      console.error('批量开启三方支付失败', error)
    } finally {
      submitLoading.value = false
    }
  }

  const disableModalOpen = ref(false)
  const disablePayChnl = ref<goodsSetting.ThirdPartyPayChannel>('xsolla_pay')
  const disableCount = computed(() => disableIdsOf(disablePayChnl.value).length)

  const openDisableModal = (payChnl: goodsSetting.ThirdPartyPayChannel) => {
    if (!disableIdsOf(payChnl).length) return
    disablePayChnl.value = payChnl
    disableModalOpen.value = true
  }

  const closeDisableModal = () => {
    disableModalOpen.value = false
  }

  // 只提交当前目标渠道的 false，不带比例，不附加另一渠道的开启动作
  const confirmDisable = async () => {
    const ids = disableIdsOf(disablePayChnl.value)
    if (!ids.length || submitLoading.value) return

    submitLoading.value = true
    try {
      const res = await batchToggleThirdParty({
        prodChnl: options.prodChnl.value,
        productList: ids.map((id) => ({ id, payChnl: disablePayChnl.value, enableThirdPay: false })),
      })
      if (!res || res.code !== '0') return
      message.success('操作成功')
      disableModalOpen.value = false
      options.clearSelection()
      await options.refresh()
    } catch (error) {
      console.error('批量关闭三方支付失败', error)
    } finally {
      submitLoading.value = false
    }
  }

  return {
    channels,
    showThirdPartyToggle,
    modalOpen,
    activePayChnl,
    priceRatio,
    preview,
    previewLoading,
    submitLoading,
    enableCounts,
    showPriceRatio,
    confirmCount,
    canConfirm,
    startEnable,
    closeModal,
    runPreview,
    confirm,
    disableCounts,
    disableModalOpen,
    disablePayChnl,
    disableCount,
    openDisableModal,
    closeDisableModal,
    confirmDisable,
  }
}
